import { toGetterName } from "./string-util.js";

/**
 * Method retrieving a property value from an object.
 * If `property` is "foo", `object.foo()`, `object.foo` and `object.getFoo()` will be tried in this order.
 *
 * @param {object} model - Object from which to retrieve the property
 * @param {string} propertyName - Property to retrieve
 * @return {*} The property value
 */
export function callGetterFor(model, propertyName) {
	const field = model[propertyName];
	if (field) {
		if (isFunction(field)) {
			return field.apply(model);
		}
		return field;
	}

	return model[toGetterName(propertyName)]();
}

/**
 * Compare two values.
 * If a value is a function, its result will be used instead.
 * If the first value has a `compareTo` method, this method will be used
 * as comparison method.
 *
 * @param {object|function} one - First value to compare
 * @param {object|function} another - Second value to compare
 * @return {boolean} True when the first value is smaller than the second one
 */
export function isSmallerOrEqualThan(one, another) {
	one = getResult(one);
	another = getResult(another);

	if (one === null || another === null) {
		return one === null;
	}

	if (isFunction(one.compareTo)) {
		return one.compareTo(another);
	}

	if (isFunction(one.toLowerCase)) {
		one = one.toLowerCase();
	}

	if (isFunction(another.toLowerCase)) {
		another = another.toLowerCase();
	}

	return one <= another;
}

/**
 * Get the result of the provided argument.
 * If the argument is a function, it will be called.
 * Otherwise, the argument will be returned.
 *
 * @param {any|function():any} argument - The argument to evaluate
 * @return {any} The result of the argument
 */
export function getResult(argument) {
	if (isFunction(argument)) {
		return argument();
	}
	return argument;
}

/**
 * Get the result of the provided argument, preserving HTML context for rendering functions.
 * If the argument is a function, returns a new function that will pass the HTML parameter through.
 * Otherwise, the argument will be returned as-is.
 *
 *  @param {any|function(html):any} argument - The argument to evaluate. Can be any value or a function that expects an HTML parameter
 * @return {any|function(html):any} If argument is a function, returns a function that accepts HTML parameter. Otherwise returns the argument itself
 */
export function getResultWithHtml(argument) {
	if (isFunction(argument)) {
		return (html) => argument(html);
	}
	return argument;
}

/**
 * Check if the provided argument is a function.
 *
 * @param {any} functionToCheck - The argument to check
 * @return {boolean}
 */
export function isFunction(functionToCheck) {
	return typeof functionToCheck === "function";
}

/**
 * Convert the provided argument to a function.
 * If the argument is a function, it will be returned.
 * Otherwise, a function returning the argument will be returned.
 *
 * @param {any} argument - The argument to convert
 * @return {function():any} The function returning the argument
 */
export function asFunction(argument) {
	if (isFunction(argument)) {
		return argument;
	}
	return function () {
		return argument;
	};
}

/**
 * Debounce a function to ensure it is not performed more that once
 * every `delay` milliseconds. When the debounced function is invoked,
 * it returns the timeoutId, so that one can call `clearTimeout` on it.
 *
 * @param {object} spec - Parameter object
 * @param {object} spec.fn - Function to debounce
 * @param {object} [spec.delay] - Minimal delay between each debounced function calls
 * @return {function} A debounced function
 * @example
 *   let debounced = debounce({
 *       fn: console.log,
 *   });
 *   debounced("first");
 *   debounced("second");
 *   debounced("third");
 *   // console.log is invoked only once, with `"third"`
 *
 *   debounced("first");
 *   sleep("500");
 *   debounced("second");
 *   // console.log is invoked twice, firstly with `"first"`, lastly with `"second"`
 */
export const debounce = (() => {
	const registry = {};
	let count = 0;

	return ({ fn, delay = 500 }) => {
		const id = `debounce-counter-${count++}`;
		return (...args) => {
			if (registry[id]) {
				clearTimeout(registry[id]);
			}

			registry[id] = setTimeout(() => {
				fn(...args);
			}, delay);

			return registry[id];
		};
	};
})();
